/**
 * How long the walk has been silent, and what that silence means.
 *
 * The strip had no clock. It said "connecting to the walk stream, which
 * usually takes a second or two" from the moment it mounted until the page was
 * closed, so a walk that never reported looked exactly like one that was about
 * to - at two seconds, and at twenty minutes. An operator watching it has no
 * way to tell a slow start from a walk that is never coming.
 *
 * The stream makes that silence normal rather than exceptional: frames are
 * emitted only when the counts or the run state change, so a run that has
 * stalled, or an executor that refused before producing a run at all, sends
 * nothing and holds the connection open. Nothing arrives to correct the
 * sentence, which is why the sentence has to correct itself.
 *
 * The refusal case is worth naming, because the dispatch route says it
 * explicitly: the executor re-proves its principal, its approved collector and
 * this bucket's registered content-reader role before reading any object, and
 * "a refusal there is reported by the walk, not pre-empted here". A walk that
 * never reports is the shape that refusal takes on this screen.
 */

export interface WalkWait {
  /** Human elapsed, e.g. "45s" or "6m 20s". */
  elapsed: string;
  /** True once the wait has passed what this screen should keep promising. */
  overdue: boolean;
  /** What to say about the wait, or null while it is still unremarkable. */
  note: string | null;
}

/** A walk that has said nothing for this long is no longer merely starting. */
export const QUIET_MS = 120_000;
/** A running walk whose counts have not moved for this long has stalled. */
export const STALL_MS = 180_000;
/** Below this, the wait is not worth a number. */
const MENTION_MS = 20_000;

export function human(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

export function walkWait(
  state: 'connecting' | 'live' | 'sealed' | 'failed' | 'none' | 'error',
  since: number,
  lastFrameAt: number | null,
  now: number,
): WalkWait {
  const waited = now - since;
  const elapsed = human(waited);

  if (state === 'connecting') {
    if (waited < MENTION_MS) {
      return { elapsed, overdue: false, note: 'connecting to the walk stream, which usually takes a second or two' };
    }
    if (waited < QUIET_MS) {
      return {
        elapsed,
        overdue: false,
        note: `connected, and no walk has reported yet after ${elapsed}`,
      };
    }
    return {
      elapsed,
      overdue: true,
      note:
        `Nothing has reported for ${elapsed}. The stream sends a frame whenever the counts change, so silence this ` +
        `long means no walk is producing any. A dispatch that was accepted still has to pass the executor's own ` +
        `checks - its principal, its approved collector, and this bucket's registered content-reader role - and a ` +
        `refusal there is reported by the walk rather than by the button. Nothing on this page can clear it.`,
    };
  }

  if (state === 'live' && lastFrameAt !== null) {
    const quiet = now - lastFrameAt;
    if (quiet < STALL_MS) return { elapsed, overdue: false, note: null };
    return {
      elapsed,
      overdue: true,
      note: `Running for ${elapsed}, and the counts have not moved for ${human(quiet)}.`,
    };
  }

  return { elapsed, overdue: false, note: null };
}
